import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { JwtService } from 'src/app/jwt.service';
import { ClienteManager } from 'src/app/managers/cliente-manager';
import { ItemManager } from 'src/app/managers/item-manager';
import { PedidoManager } from 'src/app/managers/pedido-manager';
import { SnackbarManager } from 'src/app/managers/snackbar-manager';
import { ItemDetalhado } from 'src/app/persistences/item-detalhado';
import { CartSingletonService } from 'src/app/singletons/cart-singleton.service';
import { NumberUtils } from 'src/app/utils/utils/number-utils';
import { ItemCarrinhoPaginaExcluirComponent } from '../item-carrinho-pagina/item-carrinho-pagina-excluir/item-carrinho-pagina-excluir.component';
import { ConfigService } from './../../services/config.service';
import { MessageDialogCutomizadoComponent } from '../genericos/message-dialog-cutomizado/message-dialog-cutomizado.component';

@Component({
  standalone: false,
  selector: 'app-quantidade-lista',
  templateUrl: './quantidade-lista.component.html',
  styleUrls: ['./quantidade-lista.component.css'],
})
export class QuantidadeListaComponent implements OnInit, OnChanges, OnDestroy {
  delayTime = 500;
  private btnClicked = new Subject<void>();

  @ViewChild('inputHtml')
  public inputNumber;
  // Botões Adicionar/Remover; na visualização Tabela são renderizados pela linha, em coluna própria.
  @ViewChild('tplAcoes', { static: true })
  public tplAcoes: TemplateRef<unknown>;
  @Input()
  public modoTabela = false;
  @Input()
  public temEstoque;
  @Input()
  public preco;
  @Input()
  public unMedVenda;
  @Input()
  public item: ItemDetalhado;
  @Input()
  public detalhada = false;
  @Input()
  public ignorarRotina = false;
  public valorTotalItem: number;
  @Input()
  public statusComponent = true;
  public quantidadeAtualDoItem = 0.0000;
  private cartSubscription: Subscription;
  private eraNoCarrinho = false;

  @Input()
  public quantidadeItemString;
  private ignorarValidacaoDeMultiplo = false;
  @Input()
  public estoqueDisponivel;

  public apresentaEstoqueOutrasFiliais = false;
  public temWhatsapp = false;
  public whatsapp: string;

  constructor(
    public dialog: MatDialog,
    private cartSingletonService: CartSingletonService,
    private pedidoManager: PedidoManager,
    private itemManager: ItemManager,
    private snackBarManager: SnackbarManager,
    private jwtService: JwtService,
    private clienteManager: ClienteManager,
    private configService: ConfigService
  ) {
  }

  ngOnInit(): void {
    if(this.configService.getConfig().getApresentaEstoqueDisponivelOutrasFiliais === "S"){
      this.apresentaEstoqueOutrasFiliais = true;
    }

    const contatos = JSON.parse(localStorage.getItem("contato"));
    this.temWhatsapp = contatos['temWhatsapp'];
    this.whatsapp = contatos['celularComercial'];

    this.pedidoManager.desabilitaControles.subscribe((status) => {
      this.statusComponent = status;
    });
    this.ignorarValidacaoDeMultiplo = this.itemManager.ignorarValidacaoDeMultiplo(this.item);
    this.itemManager.getEventObservable().subscribe((item) => {
      if (!this.itemManager.isEventHandled()) {
        this.item = item;
        if (!this.cartSingletonService.itemEstaNoCarrinho(this.item) && this.item.itemPedido.quantidade <= 0) {
          setTimeout(() => this.inicializarPreview(), 0);
        } else {
          this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
          if (!this.cartSingletonService.itemEstaNoCarrinho(this.item)) {
            this.atualizarInput(this.quantidadeItemString);
          } else {
            this.atualizarQtdeDoItemAtual(this.quantidadeItemString);
          }
        }
      }
      this.itemManager.setEventHandled(true);
    });

    if (this.emPreview && this.itemDisponivel() && !this.detalhada) {
      this.inicializarPreview();
    }

    if (!this.detalhada) {
      this.eraNoCarrinho = !this.emPreview;
      this.cartSubscription = this.cartSingletonService.getCarrinhoObservable().subscribe(() => this.aoMudarCarrinho());
    }

    this.configService.getConfig()
  }

  private aoMudarCarrinho(): void {
    const noCarrinho = !this.emPreview;
    if (this.eraNoCarrinho && !noCarrinho) {
      // Chamado #74296: o item saiu do carrinho; o item da pesquisa volta à unidade liberada antes do preview.
      this.itemManager.restaurarUnidadeDaPesquisa(this.item);
      setTimeout(() => this.inicializarPreview(), 0);
    }
    this.eraNoCarrinho = noCarrinho;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const mudanca = changes['quantidadeItemString'];

    // Chamado #74296: a instância reaproveitada passa a exibir outro item; sem reavaliar, aoMudarCarrinho
    // não perceberia a saída do carrinho do novo item e não restauraria a unidade da pesquisa.
    if (changes['item'] && !changes['item'].firstChange && !this.detalhada) {
      this.eraNoCarrinho = !this.emPreview;
    }

    // Pós-migração Angular 21: ao pesquisar pela tela do carrinho a busca roda duas vezes
    // (carrinho -> redirect -> home), então a lista re-renderiza com objetos novos
    // (quantidade = 0). Como o @for reaproveita esta instância, o @Input `item` é trocado
    // SEM rodar ngOnInit de novo e inicializarPreview() nunca roda para o novo item -> 0.
    // Re-inicializa o preview quando o item é trocado por um objeto novo ainda zerado.
    if (changes['item'] && !changes['item'].firstChange &&
        this.emPreview && this.itemDisponivel() && !this.detalhada &&
        this.item?.itemPedido?.quantidade <= 0) {
      this.inicializarPreview();
      return;
    }

    // Defesa adicional: impede o pai de reenviar "0" sobre um mínimo de preview já
    // estabelecido (o mesmo `quantidadeItemString` é @Input do pai e [(ngModel)] do input).
    if (!mudanca || mudanca.firstChange) {
      return;
    }
    const recebido = parseFloat(String(mudanca.currentValue ?? '').replace(',', '.'));
    const ehZeroOuInvalido = isNaN(recebido) || recebido <= 0;
    if (
      ehZeroOuInvalido &&
      this.emPreview &&
      this.itemDisponivel() &&
      !this.detalhada &&
      this.item?.itemPedido?.quantidade > 0
    ) {
      this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
      this.atualizarInput(this.quantidadeItemString);
    }
  }

  ngOnDestroy(): void {
    if (this.cartSubscription) {
      this.cartSubscription.unsubscribe();
    }
  }

  private inicializarPreview(): void {
    if (!this.itemDisponivel()) return;
    const unMedVenda = this.item.itemPedido.unMedVenda || this.item.itemDetalhes.unMedVendaPadrao;
    const primeiraTier = this.item.itemDetalhes.outrosPrecos
      .filter(p => p.unMedida === unMedVenda && p.qtMinima > 0)
      .sort((a, b) => a.qtMinima - b.qtMinima)[0];
    if (primeiraTier) {
      this.item.itemPedido.idTabelaPreco = primeiraTier.idGeral;
    }
    // Chamado #68694 (Correção D): resolve SINCRONAMENTE a tabela efetivamente aplicada
    // (a de menor preço) ANTES de definir a quantidade exibida, para o preview já abrir com a
    // quantidade correta e sem flicker (mostrar a quantidade da faixa — ex.: 3 — e depois
    // trocar para o mínimo da tabela aplicada — ex.: 1). O `primeiraTier` acima apenas semeia
    // uma quantidade de faixa para a avaliação; a tabela final vem de `getPrecoTributacao`,
    // que é síncrono e aplica a regra do menor preço (a mesma usada por `getValor`).
    this.item.itemPedido.quantidade = this.getMinimo();
    const tabelaAplicada = this.itemManager.getPrecoTributacao(this.item);
    if (tabelaAplicada?.idGeral != null) {
      this.item.itemPedido.idTabelaPreco = tabelaAplicada.idGeral;
    }
    const minimo = this.getMinimo();
    this.item.itemPedido.quantidade = minimo;
    this.quantidadeItemString = this.formatarInput(minimo);
    this.itemManager.getValor(this.item).then();
    this.atualizarInput(this.quantidadeItemString);
  }

  get emPreview(): boolean {
    return !this.cartSingletonService.itemEstaNoCarrinho(this.item);
  }

  // Chamado #74296: item do carrinho cuja unidade gravada deixou de estar liberada (Ação 159).
  // Fica visível, sem edição de quantidade, só com a opção de remover.
  get unidadeIndisponivel(): boolean {
    return !this.emPreview && this.itemManager.isUnidadeVendaIndisponivel(this.item);
  }

  adicionarAoCarrinho() {
    this.adicionarItensAoCarrinho(this.item.itemPedido.quantidade);
  }

  addQuantidadeItem(quantidade: number) {
    this.item.itemPedido.quantidade = this.calcularQuantidadeDoItem(quantidade);
    if (this.emPreview) {
      this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
      this.atualizarInput(this.quantidadeItemString);
      this.itemManager.getValor(this.item).then();
    } else {
      this.itemManager.ativarTrocaQuantidade();
      this.adicionarItensAoCarrinho(this.item.itemPedido.quantidade);
    }
  }

  adicionarItensAoCarrinho(quantidade: number) {
    //PROVISORIO, PRECISO ENTENDER ESSAS VALIDACOES MELHOR
    if(this.item.itemDetalhes.estoqueOutrasFiliais.length !== 0 && this.apresentaEstoqueOutrasFiliais && !this.itemDisponivel()){
      if (this.apresentaEstoqueOutrasFiliais && this.temWhatsapp && this.whatsapp) {
        const whatsappLink = `https://wa.me/${this.whatsapp}?text=${encodeURIComponent("Olá, gostaria de solicitar a disponibilidade do item código " + this.item.itemDetalhes.cdItem)}`;
        window.open(whatsappLink, '_blank');
      }else{
        const dialogRef = this.dialog.open(MessageDialogCutomizadoComponent, {
          width: 'auto',
          height: 'auto',
          data: { message: "Item possui estoque disponível em outra filial! Entre em contato com seu vendedor!" }
        });
        dialogRef.afterClosed().subscribe();
      }
    }else{
      if (this.clienteManager.validarClienteVendedor()) {
        if (this.item.itemPedido.quantidade <= 0) {
          this.statusComponent = false;
        }
        this.item.itemNovo = (this.item.itemPedido.quantidade === null) 
                            || (this.item.itemPedido.quantidade === undefined) 
                            || (this.item.itemPedido.quantidade <= 0);
        this.item.itemPedido.quantidade = quantidade;
        this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
        if(this.inputNumber != undefined){
          this.inputNumber.nativeElement.value = this.quantidadeItemString;
        }
        this.pedidoManager.gravarItemCarrinhoPedido(this.item).then((status) => (
          this.statusComponent = status
        ))
        .catch((error: HttpErrorResponse) => {
          this.snackBarManager.openSnackBarErro(error, 'Não foi possível salvar o item no carrinho.', 'Ok', 3000);
        });
        this.btnClicked.next();
      }
    }
  }

  atualizarQtdeDoItemAtual(quantidadeItemString: string) {    
    this.validaInput(quantidadeItemString);
  }

  removerQtdeDoItemAtual(quantidade: number) {
    if (this.clienteManager.validarClienteVendedor()) {
      const quantidadeArredondada = (this.item.itemPedido.quantidade - quantidade).toFixed(4);
      if(parseFloat(quantidadeArredondada.replace(',','.')) >= this.getMinimoControle()){
        this.item.itemPedido.quantidade = this.calcularQuantidadeDoItem(quantidade * - 1);
        this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
        if (this.emPreview) {
          this.atualizarInput(this.quantidadeItemString);
          this.itemManager.getValor(this.item).then();
        } else {
          this.itemManager.ativarTrocaQuantidade();
          this.pedidoManager.removerQuantidadeItemCarrinho(this.item);
          this.btnClicked.next();
        }
      }
    }
  }

  validaInput(quantidadeItemString: string) {
    if (this.validarQuantidadeInformada(quantidadeItemString)) {
      if (this.emPreview) {
        this.itemManager.getValor(this.item).then();
      } else {
        this.recalculaTotal();
        this.adicionarItensAoCarrinho(this.item.itemPedido.quantidade);
      }
    }
  }

  ajustaQuantidadeMaxima(quantidade: number) {
    const multiplo = this.getMultiplo();
    const sobra = this.calcularResto(quantidade % multiplo);
    this.snackBarManager.openSnackBar(
      'Quantidade máxima: ' + (this.item.itemDetalhes.qtDisponivel - sobra),
      'Ok',
      3000
    );
    this.item.itemPedido.quantidade = this.item.itemDetalhes.qtDisponivel - sobra;
    this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
    this.atualizarInput(this.quantidadeItemString);
  }

  recalculaTotal() {
    this.item.itemPedido.total = NumberUtils.roundTo(
      this.item.itemPedido.valorLiquidoIpiSt *
      (this.item.itemPedido.quantidade == null ? 0 : this.item.itemPedido.quantidade),
      2
    );
    this.valorTotalItem = this.item.itemPedido.total;
  }

  removerItemCarrinho() {
    this.pedidoManager.removerItemCarrinhoPedido(this.item).then((status) => status && this.recalculaTotal());
  }

  removerItemDoCarrinho(): boolean {
    if (this.clienteManager.validarClienteVendedor()) {
      let removeItemDoCarrinho = false;
      const dialogRef = this.dialog.open(ItemCarrinhoPaginaExcluirComponent, {
        width: '400px',
        height: '100px',
        panelClass: 'custom-dialog',
      });
      dialogRef.afterClosed().subscribe(
        (result) => {
          if (result) {
            this.removerItemCarrinho();
            removeItemDoCarrinho = true;
          }
        },
        (error) => {
          removeItemDoCarrinho = false;
          this.jwtService.validarAcessToken(error);
        }
      );
      return removeItemDoCarrinho;
    }
  }

  validarQuantidadeInformada(quantidadeItemString: string): boolean {
    const quantidade = parseFloat(quantidadeItemString.replace(',','.'));    
    if(quantidade === 0 || isNaN(quantidade)){
      return
    }
    const minimo = this.getMinimoControle();
    if (quantidade > 0 && quantidade < minimo) {
      this.snackBarManager.openSnackBar(
        'Quantidade minima de venda para este item é: ' + 
        minimo.toString(),
        'Ok',
        3000
      );
      const qtdOriginal = minimo;
      this.quantidadeItemString = this.formatarInput(qtdOriginal);
      this.item.itemPedido.quantidade = qtdOriginal;
      this.atualizarInput(this.quantidadeItemString);       
      return false;
    }

    if (!this.ignorarValidacaoDeMultiplo) {
      if (this.calcularResto(quantidade) !== 0) {
        this.item.itemPedido.quantidade = this.quantidadeAtualDoItem;        
        this.quantidadeItemString = this.formatarInput(this.quantidadeAtualDoItem);
        this.snackBarManager.openSnackBar(
          'Use apenas valores multiplos de: ' + this.getMultiplo().toString(),
          'Ok',
          3000
        );
        return false;
      }
    }
    if (quantidade > this.item.itemDetalhes.qtDisponivel) {
      this.ajustaQuantidadeMaxima(this.item.itemDetalhes.qtDisponivel);
    } else {
      this.item.itemPedido.quantidade = quantidade;
      this.atualizarInput(this.quantidadeItemString);
    }
    return true;
  }

  calcularQuantidadeDoItem(quantidade: number): number {
    let quantidadeAtual = this.item.itemPedido.quantidade;
    quantidadeAtual += quantidade;
    this.quantidadeAtualDoItem = quantidadeAtual;
    return Number(quantidadeAtual.toFixed(4));
  }

  calcularResto(quantidade: number): number {
    const quantidadeAjustada = Math.round(quantidade * 100);
    const multiploAjustado = Math.round(this.getMultiplo() * 100);
    const resto = quantidadeAjustada % multiploAjustado;
    return resto / 100;
  }

  setQuantidadeAtualDoItem($event) {
    this.inputNumber.nativeElement.select();
    this.quantidadeAtualDoItem = parseFloat($event.target.value.replace(',','.'));
  }

  botaoAdicionarVisivel(): boolean {
    return !this.cartSingletonService.itemEstaNoCarrinho(this.item) && !this.detalhada;
  }

  itemDisponivel(): boolean {
    if (this.temEstoque || this.preco <= 0 || this.item.itemDetalhes.qtDisponivel < this.item.itemDetalhes.qtdMin) {
      return false;
    }
    return true;
  }

  inputQtdeVisivel(): boolean {
    return !this.emPreview || this.itemDisponivel();
  }

  botaoAdicionarItemVisivel(): boolean {
    return !this.emPreview || this.itemDisponivel();
  }

  isQuantidadeMaxima(quantidadeString: string): boolean{
     const quantidade = parseFloat(quantidadeString.replace(',','.'));
    const quantidadeArredondada = (quantidade + this.getMultiplo()).toFixed(4);
    return parseFloat(quantidadeArredondada.replace(',','.')) > this.item.itemDetalhes.qtDisponivel;
  }

  isQuantidadeMinima(quantidadeString: string): boolean{
    const quantidade = parseFloat(quantidadeString.replace(',','.'));
    const quantidadeArredondada = (quantidade - this.getMultiplo()).toFixed(4);    
    return parseFloat(quantidadeArredondada.replace(',','.')) < this.getMinimoControle();
  }  

  getMinimo(): number{
    return this.itemManager.getMinimo(this.item).minimo;
  } 

  getMinimoControle(): number{
    return this.itemManager.getMinimo(this.item).minimoControle;
  } 

  getMultiplo(): number{   
    return this.itemManager.getMultiplo(this.item);
  }

  barrarInputsIncorretos(input){
     const pattern = /^[0-9,.]*$/
     const inputDoUsuario = String.fromCharCode(input.charCode);
     if(!pattern.test(inputDoUsuario)){
      input.preventDefault();
    }
  }

  atualizarInput(quantidadeItemString:string){    
    if( this.inputNumber != undefined){
      this.inputNumber.nativeElement.value = quantidadeItemString;
    }
  }  

  onBlur(quantidadeItemString:string ){
    if(quantidadeItemString.trim() === '' || quantidadeItemString.trim() === '0' || quantidadeItemString.trim() === '0,'){
      this.quantidadeItemString = this.formatarInput(this.item.itemPedido.quantidade);
      this.inputNumber.nativeElement.value = this.quantidadeItemString;
    }
  }

  formatarInput(quantidade: number):string {    
    return quantidade.toLocaleString('pt-BR',{useGrouping: false}).replace('.',',');
  }

  /** Atalho de teclado "+": mesma regra do botão de adicionar quantidade. */
  incrementarPorTeclado(): void {
    // Chamado #74296: item com unidade de venda bloqueada não tem a quantidade editada (nem por atalho de teclado).
    if (this.inputQtdeVisivel() && this.statusComponent && !this.unidadeIndisponivel && !this.isQuantidadeMaxima(this.quantidadeItemString)) {
      this.addQuantidadeItem(this.getMultiplo());
    }
  }

  /** Atalho de teclado "-": mesma regra do botão de remover quantidade. */
  decrementarPorTeclado(): void {
    // Chamado #74296: item com unidade de venda bloqueada não tem a quantidade editada (nem por atalho de teclado).
    if (this.inputQtdeVisivel() && this.statusComponent && !this.unidadeIndisponivel && !this.isQuantidadeMinima(this.quantidadeItemString)) {
      this.removerQtdeDoItemAtual(this.getMultiplo());
    }
  }

  /** Atalho de teclado Enter: mesma ação (e mesma condição de habilitação) do botão Adicionar. */
  adicionarPorTeclado(): void {
    if (!this.botaoAdicionarVisivel() || !this.statusComponent) {
      return;
    }
    const semOutraFilial = !this.apresentaEstoqueOutrasFiliais || this.item.itemDetalhes.estoqueOutrasFiliais.length === 0;
    if (!this.itemDisponivel() && semOutraFilial) {
      return;
    }
    this.adicionarAoCarrinho();
  }

  /** Mostra o aviso de quantidade acima do estoque ou sem tabela de preço (mesma condição do rótulo da quantidade). */
  avisoQuantidade(): boolean {
    return this.item.itemPedido.quantidade > this.item.itemPedido.quantidadeEstoque
      || this.item.itemDetalhes.outrosPrecos.length === 0
      || this.unidadeIndisponivel;
  }

  onClickQuantidade() {
    const mensagem = this.unidadeIndisponivel
      ? `Unidade de venda (${this.item.itemPedido.unMedVenda}) indisponível para este item. Remova-o do pedido para continuar.`
      : 'Quantidade de estoque ou tabela de preço indisponível!';
    this.snackBarManager.openSnackBarCustom(
      mensagem,
      'Ok',
      5000
    );
  }

}
